import React from 'react';
import { Box, Collapse, LinearProgress } from '@mui/material';
import { Surface } from './Surface';

export interface PageToolbarProps {
  /** Left of the top layer: what the page is showing — date range, scope. */
  start?: React.ReactNode;
  /** Right of the top layer: what you can do — freshness, export, panels. */
  end?: React.ReactNode;
  /** The lower layer, under a hairline — usually a `FilterBar`. */
  children?: React.ReactNode;
  /** Show the lower layer. Pair with `useCollapseOnScroll` to fold it away on scroll. */
  secondaryOpen?: boolean;
  /** Stay at the top of the scrolling area. On by default. */
  sticky?: boolean;
  /** A thin progress line along the bottom edge while the page reloads. */
  loading?: boolean;
  /**
   * How far a long job has got, 0–100, drawn in place of the indeterminate
   * line. For work the reader started and is waiting on — refreshing every
   * widget — where a bar that only says "busy" says less than it could.
   */
  progress?: number;
  ariaLabel?: string;
  /** Id for the lower layer, so a button can say it controls it. */
  secondaryId?: string;
}

/**
 * The bar at the top of a dashboard or report: one card, two layers.
 *
 * The top layer holds what you are looking at on the left and what you can do
 * on the right. The lower layer holds filters, and can fold away. Sticky, so
 * the range and the filters stay in reach on a long page.
 *
 * When space runs out, the right side wraps onto its own line rather than
 * squeezing the left.
 */
export const PageToolbar = React.forwardRef<HTMLElement, PageToolbarProps>(function PageToolbar(
  {
    start,
    end,
    children,
    secondaryOpen = true,
    sticky = true,
    loading = false,
    progress,
    ariaLabel = 'Page controls',
    secondaryId,
  },
  ref,
) {
  return (
    <Surface
      ref={ref as React.Ref<HTMLDivElement>}
      component="section"
      aria-label={ariaLabel}
      sx={(t) => ({
        position: sticky ? 'sticky' : 'relative',
        top: 0,
        zIndex: t.zIndex.appBar - 1,
        overflow: 'hidden',
      })}
    >
      {/* The end stays on the right however much the start holds: the start
          wraps within its own space instead of pushing the end to a new line.
          On a phone there is no room for both, so the end drops below rather
          than squeezing the controls and running over them. */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1.5, px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, minWidth: 0, flex: '0 1 auto' }}>
          {start}
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{end}</Box>
      </Box>

      {children && (
        <Collapse in={secondaryOpen} id={secondaryId}>
          <Box sx={(t) => ({ borderTop: `1px solid ${t.tokens.border.subtle}`, px: 1.5, pt: 1.25, pb: 1.5 })}>
            {children}
          </Box>
        </Collapse>
      )}

      {(loading || progress !== undefined) && (
        <LinearProgress
          variant={progress === undefined ? 'indeterminate' : 'determinate'}
          value={progress}
          sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2 }}
        />
      )}
    </Surface>
  );
});
